import { useEffect, useState, useCallback } from "react"
import "cordova-plugin-purchase"
import { settings } from "@/settings"
import * as Sentry from "@sentry/react"
import { ApiError } from "@/lib/vibrato-api"

// Retry function with exponential backoff
const retryWithBackoff = async <T,>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error

      if (attempt === maxRetries) {
        throw lastError
      }

      // Calculate delay with exponential backoff: baseDelay * 2^attempt
      const delay = baseDelay * Math.pow(2, attempt)
      console.log(`Attempt ${attempt + 1} failed, retrying in ${delay}ms...`, error)

      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  throw lastError!
}

export function useAppleSubs({ userSession, vibratoClient }: { userSession: any; vibratoClient: any }) {
  if (!settings.IS_IOS) {
    return undefined
  }

  const [store, setStore] = useState<CdvPurchase.Store | undefined>(undefined)
  const [plusProduct, setPlusProduct] = useState<CdvPurchase.Product | undefined>(undefined)
  const [platform, setPlatform] = useState<any | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [processingNewSub, setProcessingNewSub] = useState(false)
  const [transactionError, setTransactionError] = useState<string | undefined>(undefined)
  const [restoringSubscription, setRestoringSubscription] = useState(false)

  const registerAppleSubscriptionWithRetry = useCallback(
    async (subscriptionData: { originalTransactionId: string; transactionId: string; productId: string }) => {
      return retryWithBackoff(
        () => vibratoClient.registerAppleSubscription(subscriptionData),
        3, // max 3 retries
        1000 // 1 second base delay
      )
    },
    [vibratoClient]
  )

  const restoreSubscription = useCallback(async () => {
    if (!store || !platform) {
      console.error("Store or platform not initialized")
      setTransactionError("Store not ready for restoration")
      return
    }

    if (restoringSubscription) {
      console.log("Restoration already in progress")
      return
    }

    setRestoringSubscription(true)
    setTransactionError(undefined)

    try {
      console.log("Starting subscription restoration...")

      // Request restoration of purchases using the correct method
      store.restorePurchases()

      console.log("Restoration request completed")

      // The restoration will trigger the existing finishPurchase callback
      // for any valid restored transactions, so we don't need to handle them here
    } catch (error: any) {
      console.error("Error during subscription restoration:", error)
      setTransactionError("Failed to restore subscription. Please try again.")
      Sentry.captureException(error)
    } finally {
      // Set a timeout to reset the restoring state since the store
      // doesn't always provide a clear completion callback
      setTimeout(() => {
        setRestoringSubscription(false)
      }, 5000)
    }
  }, [store, platform, restoringSubscription])

  const finishPurchase = useCallback(
    async (transaction: CdvPurchase.AppleAppStore.SKTransaction) => {
      console.log("Apple subscription purchase details:", {
        transactionId: transaction.transactionId,
        products: transaction.products,
        state: transaction.state,
        purchaseDate: transaction.purchaseDate,
        expirationDate: transaction.expirationDate,
        purchaseId: transaction.purchaseId,
        transaction: transaction,
        originalTransactionId: transaction?.originalTransactionId
      })

      // Filter out system/initialization transactions
      if (transaction.transactionId === "appstore.application" || transaction.transactionId?.startsWith("appstore.")) {
        console.log("Skipping system transaction:", transaction.transactionId)
        transaction.finish()
        return
      }

      // Filter out non-plus transactions
      const product = transaction.products[0]
      if (product.id !== "plus") {
        console.log("Skipping non-plus transaction:", product)
        transaction.finish()
        return
      }

      if (transaction.state == "cancelled") {
        setTransactionError("The transaction was cancelled")
        setProcessingNewSub(false)
        setRestoringSubscription(false)
        return
      }

      if (transaction.state != "approved") {
        console.log("Skipping non-approved transaction:", transaction)
        transaction.finish()
        return
      }

      var originalTransactionId = transaction?.originalTransactionId
      if (!originalTransactionId) {
        console.log("No original transaction ID, using transaction ID", transaction.transactionId)
        originalTransactionId = transaction.transactionId
      }

      const plan = await vibratoClient.getPlan()
      console.log("Current user plan before finalizing transaction", plan)

      try {
        const resp = await registerAppleSubscriptionWithRetry({
          originalTransactionId: originalTransactionId,
          transactionId: transaction.transactionId,
          productId: product.id
        })

        console.log("Apple subscription purchase registered with backend", resp)

        await transaction.finish()
        console.log("Apple subscription finished:", transaction)

        if (processingNewSub) {
          console.log("Finalized new subscription, redirecting to new subscription page")
          setProcessingNewSub(false)
          setTransactionError(undefined) // Clear any errors on success
          window.location.href = "/new-subscription"
        }

        if (restoringSubscription) {
          console.log("Successfully restored subscription")
          setRestoringSubscription(false)
          setTransactionError(undefined) // Clear any errors on success
          // Refresh the page to update the UI with the restored subscription
          window.location.reload()
        }
      } catch (error: any) {
        if (error.message.includes("already exists")) {
          console.log("Apple subscription already exists in backend, finishing transaction")
          transaction.finish()
          setProcessingNewSub(false)
          setRestoringSubscription(false)
          setTransactionError(undefined) // Clear any errors on success
          return
        }

        console.error("Error registering Apple subscription after retries", error)
        if (error instanceof ApiError && error.data) {
          setTransactionError(
            `An error occurred while registering the subscription: ${error.message}. Details: ${JSON.stringify(error.data)}`
          )

          Sentry.setContext("error_data", error.data)
        } else {
          setTransactionError(`An error occurred while registering the subscription: ${error.message}`)
        }

        setProcessingNewSub(false)
        setRestoringSubscription(false)

        Sentry.captureException(error)
      }
    },
    [vibratoClient, setProcessingNewSub, processingNewSub, restoringSubscription, registerAppleSubscriptionWithRetry]
  )

  const refreshProduct = useCallback(() => {
    if (!store) return

    // Get updated product information
    const updatedPlusProduct = store.get("plus", platform?.APPLE_APPSTORE)
    setPlusProduct(updatedPlusProduct)

    console.log("Updated product state:", {
      id: updatedPlusProduct?.id,
      owned: updatedPlusProduct?.owned,
      price: updatedPlusProduct?.pricing?.price,
      currency: updatedPlusProduct?.pricing?.currency
    })

    if (userSession?.plan === "freemium" && updatedPlusProduct?.owned) {
      console.log("User has a plus subscription, reloading page")
      window.location.reload()
    }
  }, [store, platform, userSession, setPlusProduct])

  const purchasePlus = useCallback(() => {
    if (!plusProduct) {
      console.error("Plus product not found")
      return
    }

    if (plusProduct.owned) {
      console.log("User already owns plus subscription")
      return
    }

    const offer = plusProduct.getOffer()
    if (offer) {
      setProcessingNewSub(true)
      setTransactionError(undefined) // Clear any previous errors
      offer.order().catch((error) => {
        setTransactionError("An error occurred while processing the transaction")
        setProcessingNewSub(false)
        Sentry.captureException(error)
      })
    }
  }, [plusProduct, setProcessingNewSub, processingNewSub])

  useEffect(() => {
    if (!vibratoClient) {
      return
    }

    setLoading(true)
    const { store, ProductType, Platform } = CdvPurchase
    setPlatform(Platform)

    store.register([
      {
        type: ProductType.PAID_SUBSCRIPTION,
        id: "plus",
        platform: Platform.APPLE_APPSTORE
      }
    ])

    store.initialize([Platform.APPLE_APPSTORE])
    setStore(store)

    console.log("Configured Apple subscription purchases")
  }, [vibratoClient])

  useEffect(() => {
    if (!store) return

    store.error((error) => {
      setProcessingNewSub(false)
      console.error("Apple subscription purchase error", error)
      Sentry.captureException(error)
    })

    // @ts-ignore
    store.when().productUpdated(refreshProduct).approved(finishPurchase)

    store.ready(() => {
      const plusProduct = store.get("plus", platform?.APPLE_APPSTORE)
      console.log("Plus product", plusProduct)
      setPlusProduct(plusProduct)
      setLoading(false)

      store.products.map((product) => {
        console.log("Available Subscription Product", product)
      })
    })

    return () => {
      store.off(refreshProduct)
      store.off(finishPurchase)
    }
  }, [store, platform, refreshProduct, finishPurchase])

  return {
    store,
    plusProduct,
    purchasePlus,
    loading,
    processingTransaction: processingNewSub,
    transactionError,
    restoreSubscription,
    restoringSubscription
  }
}
